import React from "react";
import classes from "../style.module.scss";

export default () => (
    <>
        <div className={classes.faq}>
            <p>
                <strong>Last updated: 2026-03-30</strong>
            </p>
            <h3>1. Overview & General</h3>
            <p>
                These terms and conditions (the "Terms") shall govern the legal relationship between the user ("you")
                and Volta Research Inc. ("Volta"), a corporation incorporated under the laws of Canada and located at
                1655 Dupont St, Suite 101, Toronto, Ontario M6P 3T1, in respect of your use of the Volta SNAP{" "}
                <a rel="noopener noreferrer" target="_blank" href="https://app.voltasnap.ca/login">
                    application
                </a>{" "}
                (the "App"), an energy modelling platform that generates insights into the energy performance of
                buildings, and the Volta{" "}
                <a rel="noopener noreferrer" target="_blank" href="https://voltaresearch.org/">
                    website
                </a>{" "}
                (the "Website") (together, the "Platform"). All references to the Platform shall include both the App
                and the Website, and apply to both, each, or either, as applicable.
            </p>
            <p>
                Your use of Volta's App and/or Website signifies and constitutes your acceptance of and agreement to be
                bound by these Terms. If you do not agree to these Terms, you must immediately cease all use of the
                Platform.
            </p>
            <p>
                Use of the App is also subject to Volta's Software Licensing Agreement ("SLA"), which governs access,
                permitted use, and payment for paid features. In the event of any conflict or inconsistency between
                these Terms and the SLA with respect to the App, the SLA shall prevail to the extent of such conflict or
                inconsistency. For clarity, these Terms shall continue to govern all other aspects of your use of the
                Platform not addressed in the SLA.
            </p>
            <p>
                For information regarding Volta's personal data processing policy, please refer to Volta's Website
                Privacy Policy (
                <a rel="noopener noreferrer" target="_blank" href="https://voltaresearch.org/privacy-policy">
                    voltaresearch.org/privacy-policy
                </a>
                ) if you are a visitor to the Website, or Volta's Volta SNAP Privacy Policy (
                <a rel="noopener noreferrer" target="_blank" href="https://app.voltasnap.ca/resources/privacy">
                    app.voltasnap.ca/resources/privacy
                </a>
                ) if you are a user of the App.
            </p>
            <p>
                Volta reserves the right to modify these Terms at any time in its sole discretion. Volta will provide
                notice of material changes by posting the updated Terms on the Website with a revised "Last Updated"
                date and/or by sending notice to the email address associated with your account (if applicable). Your
                continued use of the Platform following such notice constitutes your acceptance of the modified Terms.
                If you do not agree to any modification, you must immediately cease using the Platform. It is your
                responsibility to review these Terms periodically.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>2. Eligibility & Authority</h3>
            <p>
                By using Volta's Platform, or any part thereof, you represent, warrant and covenant that: (a) you are at
                least 18 years of age (or the age of majority in your jurisdiction, whichever is greater); (b) you have
                the legal capacity and authority to enter into and be bound by these Terms; (c) if you are accepting
                these Terms on behalf of an organization, you have the authority to bind that organization to these
                Terms; and (d) your use of the Platform does not violate any applicable law or regulation.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>3. Permitted Usage & License</h3>
            <p>
                Subject to your compliance with these Terms and any applicable SLA, Volta hereby grants you a limited,
                non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Platform
                solely for your professional and lawful use in connection with research and evaluating or analyzing the
                energy performance of buildings. This license does not permit resale, redistribution, or commercial
                exploitation of the Platform or its outputs for the benefit of third parties without Volta's prior
                written consent.
            </p>
            <p>You must not, directly or indirectly:</p>
            <ol type="a">
                <li>copy, modify, adapt, translate, or create derivative works of any part of the Platform;</li>
                <li>
                    reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code or
                    underlying algorithms of the Platform;
                </li>
                <li>
                    use any automated means to access the Platform for any purpose without Volta's express written
                    consent;
                </li>
                <li>
                    use the Platform in any manner that could damage, disable, overburden, or impair its operation or
                    interfere with any other party's use;
                </li>
                <li>
                    use the Platform to develop or provide any product or service that competes with the Platform; or
                </li>
                <li>
                    use the Platform for any unlawful or unauthorized purpose, including any activity that violates
                    applicable laws, regulations, or professional standards.
                </li>
            </ol>
            <p>
                All rights, title, and interest in and to the Platform, including all content, software, databases,
                algorithms, and associated intellectual property, remain Volta's exclusive property. Except for the
                limited license expressly granted above, no rights are transferred to you.
            </p>
            <p>
                Volta reserves the right, in its sole discretion and without liability, to suspend, restrict, or
                terminate your access to the Platform at any time, with or without notice, if Volta reasonably believes
                that you have: (a) violated these Terms or any applicable SLA; (b) engaged in conduct that may harm or
                create liability for Volta, the Platform, or other users; (c) engaged in fraudulent, abusive, or illegal
                activity; or (d) for any other reason in Volta's business judgment. Upon termination, your license to
                use the Platform immediately ceases, and you must immediately cease all use of the Platform. Volta shall
                have no liability to you for any suspension or termination of access.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>4. Indemnity</h3>
            <p>
                You agree to indemnify, defend, and hold harmless Volta and its officers, directors, shareholders,
                employees, agents, affiliates, successors, assigns, and third-party service providers (collectively, the
                "Indemnified Parties") from and against any and all claims, demands, actions, suits, proceedings,
                losses, liabilities, damages, costs, and expenses (including reasonable attorneys' fees and court costs)
                of any kind or nature, known and unknown, arising out of or in connection with: (a) your use or misuse
                of the Platform; (b) your violation of these Terms or any applicable law or regulation; (c) your User
                Content (as defined below) or any content you submit to the Platform; (d) your violation of any
                third-party rights, including intellectual property rights or privacy rights; or (e) any negligent or
                willful misconduct by you. You agree to cooperate fully in the defense of any such claim. Volta reserves
                the right to assume the exclusive defense and control of any matter subject to indemnification by you,
                at your expense.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>5. User Content & Data</h3>
            <p>
                As between you and Volta, you retain all ownership rights to any project data, models, or other
                information you submit to the Platform ("User Content"), subject to the license granted to Volta herein.
                You represent and warrant that: (a) you own or have the necessary rights to submit the User Content; (b)
                the User Content does not infringe any third-party intellectual property rights or violate any
                applicable law; and (c) the User Content does not contain any viruses, malware, or harmful code.
            </p>
            <p>
                By submitting User Content, you grant Volta a non-exclusive, royalty-free, worldwide, perpetual,
                irrevocable license to use, host, store, reproduce, modify, communicate, publish, publicly perform,
                publicly display, and distribute such content solely for the purposes of: (a) providing, maintaining,
                and improving the Platform and Volta's services; (b) developing new features and services; and (c)
                creating aggregated or de-identified data for analytical, statistical, research, and machine learning
                purposes. Volta may retain and use such aggregated or de-identified data indefinitely, provided such
                data cannot reasonably be used to identify you or any individual. You acknowledge that Volta's machine
                learning models and algorithms may be trained using de-identified User Content and that Volta owns all
                rights to such models and algorithms.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>6. Intellectual Property</h3>
            <p>
                We have the right to use all the content on Volta's Platform, and all intellectual property rights
                therein, and all such content is protected under copyright law.
            </p>
            <p>
                Volta trademarks and design marks (collectively, the "Marks"), are trademarks or registered Volta
                trademarks, and Volta property.
            </p>
            <p>
                All other products, brand and company names and logos used, displayed or referenced on Volta's Platform
                may be the trademarks or registered trademarks of their respective owners. Any use of any trademarks,
                including the Marks, without the express written consent of the owner of the trademark is strictly
                prohibited.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>7. Disclaimer</h3>
            <p>
                The energy modelling insights and results generated by the Platform, in whole or in part, are provided
                for informational purposes only and should not be relied upon as professional engineering,
                architectural, or consulting advice. Volta makes no representations or warranties of any kind, express
                or implied, as to the accuracy, completeness, reliability, or suitability of any output generated by the
                Platform. You acknowledge that the Platform's outputs are estimates based on the data and assumptions
                you provide, and actual results may vary significantly. You assume all risk and responsibility for your
                use of the Platform and any decisions made based on its outputs. You should consult with qualified
                professionals before making any decisions based on the Platform's outputs.
            </p>
        </div>
    </>
);
